import { useEffect, useRef } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  orderFollowedByRecency,
  type AthleteTargets,
  type CurrentMax,
  type Exercise,
  type Program as ProgramT,
  type ProgramProgress as ProgramProgressT,
  type UnitsPreference,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { NestedHScrollView } from "../primitives/NestedHScrollView";
import { PagerNav } from "../primitives/PagerNav";
import { usePagerScroll } from "../primitives/usePagerScroll";
import type { PlanGapsLauncher } from "../Progress/planGaps";
import { ProgramRunCard } from "./ProgramRunCard";

// The Plan highlight: a swipeable pager over the FOLLOWED programs, one
// ProgramRunCard per page (Antenore 2026-06-18). It replaces BOTH the single
// "today" hero AND the lower "In corso" list — those duplicated each other,
// and the hero had no day selection while the list card did. Reusing
// ProgramRunCard verbatim means every per-program control (next-up, Start,
// "Change week or session" day picker, View program, Edit, Unfollow) already
// works; the carousel adds the pager plus the shared PagerNav (arrows +
// dots + counter — essential on web, where a hidden-scrollbar pager can't
// be mouse-dragged; the controller logic lives in usePagerScroll, shared
// with the Home PR gallery since HR-9b).
//
// Order = orderFollowedByRecency: the starred program first (#393), then the
// most-active cycle. One followed program → no nav, no paging
// (degrades to a single card). Zero → the component renders nothing and the
// host falls back to HomeHero's blank state.
export function ProgramHighlightCarousel({
  followedPrograms,
  progress,
  catalog,
  resolveName,
  unit,
  theme,
  dark,
  onStart,
  startingId,
  onUnfollow,
  onEdit,
  readOnlyProgramIds,
  onStopFollowing,
  currentProgramId,
  onSetCurrentBlock,
  maxMeta,
  athleteTargets,
  onPlanGaps,
}: {
  followedPrograms: ProgramT[];
  progress: Record<string, ProgramProgressT | null> | null;
  catalog: Exercise[] | null;
  resolveName: (slug: string, snapshot?: string) => string;
  unit: UnitsPreference;
  theme: UiTheme;
  dark: boolean;
  /** Mobile-only: starts (program, weekIdx, sessionIdx) on the logger. Omit
   *  on hosts with no logger (web) — ProgramRunCard hides the run affordances. */
  onStart?: (program: ProgramT, weekIdx: number, sessionIdx: number) => void;
  startingId?: string | null;
  onUnfollow: (program: ProgramT) => void;
  onEdit: (program: ProgramT) => void;
  /** Ids the caller FOLLOWS (useProgramFollowGate): those cards are
   *  read-only, so no pencil, and their ✕ calls `onStopFollowing` instead
   *  of `onUnfollow`. Absent ⇒ no card is read-only and every control
   *  behaves exactly as before. */
  readOnlyProgramIds?: ReadonlySet<string>;
  /** Drop the follow row. Only ever called for a read-only card; absent ⇒
   *  the ✕ falls back to `onUnfollow`, which is what a host that has not
   *  been taught about follows already does. */
  onStopFollowing?: (program: ProgramT) => void;
  /** AP-3: the athlete's pinned "active block" id (Profile.currentProgramId).
   *  Its card comes first and shows its star filled (#393). */
  currentProgramId?: string | null;
  /** Toggle a program as the active block. Omit to hide the star (web). */
  onSetCurrentBlock?: (program: ProgramT) => void;
  /** The caller's MAXes, as the host holds them: the preview's bands (#358). */
  maxMeta?: ReadonlyMap<string, CurrentMax>;
  /** #393: the athlete's weekly targets; each card says which it covers. */
  athleteTargets?: AthleteTargets | null;
  /** #492 F5: «plan the gaps» on each card (ProgramRunCard). */
  onPlanGaps?: PlanGapsLauncher;
}) {
  const { t } = useTranslation();
  const ordered = orderFollowedByRecency(followedPrograms, progress, currentProgramId);
  const pager = usePagerScroll(ordered.length);
  // #393: a star tap reorders the cards; the pager follows the tapped one to
  // its new page instead of staying on a page that now holds another program.
  // Armed with the pin the tap asks for: any other arriving value (a failed
  // write, then a change from elsewhere) disarms it without moving.
  const followRef = useRef<{ id: string; expected: string | null } | null>(null);
  useEffect(() => {
    const follow = followRef.current;
    if (follow === null) return;
    followRef.current = null;
    if ((currentProgramId ?? null) !== follow.expected) return;
    const i = ordered.findIndex((p) => p.id === follow.id);
    if (i >= 0) pager.goTo(i);
    // Only the star's change moves the pager: `ordered` is a new array each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProgramId]);
  const styles = makeStyles(theme);

  if (ordered.length === 0) return null;
  const multi = ordered.length > 1;

  return (
    <View style={styles.wrap} onLayout={pager.onLayout}>
      <NestedHScrollView
        ref={pager.scrollRef}
        horizontal
        pagingEnabled={multi}
        scrollEnabled={multi}
        showsHorizontalScrollIndicator={false}
        onScroll={pager.onScroll}
        scrollEventThrottle={16}
        // Lock to the horizontal axis so a vertical drag on a card scrolls the
        // page, not the pager. The card's heatmap does scroll sideways: both
        // are NestedHScrollView, so the heatmap wins a drag that starts on it.
        directionalLockEnabled
      >
        {pager.width > 0
          ? ordered.map((p, i) => {
              // ONE derivation per card, read by the pencil and by the ✕.
              const readOnly = readOnlyProgramIds?.has(p.id) ?? false;
              return (
              // Each page is the full viewport width (so paging snaps one card
              // at a time) but insets the card by s.s5 — the same horizontal
              // padding ProgramsPlanView's wrap gave these cards in the list,
              // so the carousel card lines up with the rest of the Plan content
              // instead of running edge-to-edge (Antenore 2026-06-18).
              // Off-screen pages leave the a11y tree — PagerNav is the screen
              // reader's navigation, not wandering onto invisible cards.
              <View
                key={p.id}
                style={[styles.page, { width: pager.width }]}
                importantForAccessibility={
                  i === pager.index ? "auto" : "no-hide-descendants"
                }
                accessibilityElementsHidden={i !== pager.index}
              >
                <ProgramRunCard
                  program={p}
                  progress={progress?.[p.id] ?? null}
                  catalog={catalog}
                  resolveName={resolveName}
                  unit={unit}
                  isStarting={startingId === p.id}
                  onStart={
                    onStart
                      ? (weekIdx, sessionIdx) => onStart(p, weekIdx, sessionIdx)
                      : undefined
                  }
                  onUnfollow={() =>
                    readOnly && onStopFollowing
                      ? onStopFollowing(p)
                      : onUnfollow(p)
                  }
                  onEdit={() => onEdit(p)}
                  readOnly={readOnly}
                  isCurrentBlock={currentProgramId === p.id}
                  onSetCurrentBlock={
                    onSetCurrentBlock
                      ? () => {
                          followRef.current = {
                            id: p.id,
                            expected: currentProgramId === p.id ? null : p.id,
                          };
                          onSetCurrentBlock(p);
                        }
                      : undefined
                  }
                  maxMeta={maxMeta}
                  active={i === pager.index}
                  athleteTargets={athleteTargets}
                  onPlanGaps={onPlanGaps}
                  theme={theme}
                  dark={dark}
                />
              </View>
              );
            })
          : null}
      </NestedHScrollView>

      <View style={styles.navInset}>
        <PagerNav
          theme={theme}
          index={pager.index}
          count={ordered.length}
          onGoTo={pager.goTo}
          prevLabel={t("mobile.plan.carouselPrev", {
            defaultValue: "Previous program",
          })}
          nextLabel={t("mobile.plan.carouselNext", {
            defaultValue: "Next program",
          })}
          positionLabel={t("mobile.plan.carouselPosition", {
            n: pager.index + 1,
            total: ordered.length,
            defaultValue: "Program {{n}} of {{total}}",
          })}
          dotLabel={(i) =>
            t("mobile.plan.carouselPosition", {
              n: i + 1,
              total: ordered.length,
              defaultValue: "Program {{n}} of {{total}}",
            })
          }
        />
      </View>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { spacing: s } = theme;
  return StyleSheet.create({
    // The carousel renders chrome-less inside HomeView's hero box (HR-9), so
    // it carries its own vertical padding; the s5 page inset below doubles as
    // the box's horizontal padding.
    wrap: { width: "100%", paddingVertical: s.s4 },
    page: { paddingHorizontal: s.s5 },
    // PagerNav row inside the hero box — same s5 inset as the pages so a
    // long dots row can't run into the box border.
    navInset: { marginTop: s.s2, paddingHorizontal: s.s5 },
  });
}
